import { AnalysisType, projectAnalysis, UnitType } from "@/types/analysis/ProjectAnalysis";
import { FeatureManagementType } from "@api/apiClient";
import { useProjectData } from "@api/analysis";
import { useBenchmarkAndTargets } from "@api/benchmarkCatalog";
import { ChartOverlay } from "@components/Charts/BarChart";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { useTheme } from "@mui/material";
import { useMemo } from "react";

/**
 * Shared hook that returns benchmark/target overlay lines for any bar chart.
 *
 * - **By Scenario** → WholeBuilding benchmark + target (full-width lines).
 * - **By Model**    → Per-model-type benchmark + target (lines scoped to each model type's bars).
 *
 * Matching uses `modelTypeId` / `modelTypeEntityId` (the FK to BenchmarkModelType
 * MetadataEntity), not the scope string.
 *
 * @param analysisType The analysis type powering the current chart (e.g. CadModelCategory).
 */
export function useBenchmarkOverlays(analysisType: AnalysisType): ChartOverlay[] {
  const appStore = useAppStore();
  const theme = useTheme();
  const isBenchmarkEnabled = appStore.hasFeature(FeatureManagementType.BenchmarkCatalog);
  const hasAdditionalMeta = appStore.hasFeature(FeatureManagementType.AdditionalMetadata);
  const projectId = appStore.project?.projectId;

  const benchmarkColor = theme.palette.warning.main;
  const targetColor = theme.palette.success.main;

  const { data: benchmarkData } = useBenchmarkAndTargets(
    projectId ? String(projectId) : null,
    { enabled: isBenchmarkEnabled && !!projectId },
  );

  const { data: projectData } = useProjectData(
    projectId ? String(projectId) : undefined,
    { enabled: isBenchmarkEnabled && projectAnalysis.isGroupTypeModel && !!projectId },
  );

  return useMemo<ChartOverlay[]>(() => {
    if (!hasAdditionalMeta) return [];

    const unit = projectAnalysis.selectedUnitType;
    if (unit !== UnitType.KgCO2ePerM2 && unit !== UnitType.KgCO2ePerM2PerYear) return [];

    const unitLabel = unit === UnitType.KgCO2ePerM2PerYear ? "kgCO\u2082e/m\u00B2/yr" : "kgCO\u2082e/m\u00B2";

    const analysisData = projectAnalysis.getAnalysis(analysisType);
    const representativeServiceLife =
      unit === UnitType.KgCO2ePerM2PerYear
        ? (analysisData
            ? Array.from(analysisData.scenarioAnalyses.values())[0]?.serviceLife ?? 1
            : 1)
        : 1;

    const toValue = (kgPerM2: number | undefined): number | null => {
      if (kgPerM2 == null) return null;
      return unit === UnitType.KgCO2ePerM2PerYear ? kgPerM2 / representativeServiceLife : kgPerM2;
    };

    const benchmarkValues = benchmarkData?.benchmarkValues ?? [];
    const targetValues = benchmarkData?.targetValues ?? [];
    const result: ChartOverlay[] = [];

    if (projectAnalysis.isGroupTypeModel && projectData) {
      // By Model: per-model-type overlays
      // Build modelId → { modelTypeId, modelTypeName } from project data
      const modelIdToTypeInfo = new Map<string, { typeId: string; typeName: string }>();
      projectData.scenarioDatas?.forEach((sd) => {
        sd.modelDatas?.forEach((md) => {
          if (md.model?.modelId && md.model?.modelTypeId) {
            modelIdToTypeInfo.set(md.model.modelId, {
              typeId: md.model.modelTypeId,
              typeName: md.model.modelTypeName ?? "",
            });
          }
        });
      });

      // Map each bar directly to its modelTypeId and group indices in one pass
      const typeIdToIndices = new Map<string, number[]>();
      let barIdx = 0;
      analysisData?.scenarioAnalyses.forEach((sa) => {
        sa.groupData.forEach((gd) => {
          const typeId = modelIdToTypeInfo.get(gd.id as string)?.typeId;
          if (typeId) {
            if (!typeIdToIndices.has(typeId)) typeIdToIndices.set(typeId, []);
            typeIdToIndices.get(typeId)!.push(barIdx);
          }
          barIdx++;
        });
      });

      for (const [modelTypeId, indices] of typeIdToIndices) {
        // Resolve display name from benchmark/target DTOs (which have it populated),
        // falling back to the model's own modelTypeName field
        const bmMatch = benchmarkValues.find((v) => v.modelTypeEntityId === modelTypeId);
        const tgtMatch = targetValues.find((t) => t.modelTypeEntityId === modelTypeId);
        const modelInfo = Array.from(modelIdToTypeInfo.values()).find((i) => i.typeId === modelTypeId);
        const displayName = bmMatch?.modelTypeName || tgtMatch?.modelTypeName || modelInfo?.typeName || modelTypeId;

        // Match benchmark value by modelTypeEntityId FK
        const bm = benchmarkValues.find((v) => v.modelTypeEntityId === modelTypeId);
        const bmVal = toValue(bm?.valueKgCo2ePerM2);
        if (bmVal != null) {
          result.push({
            label: `${i18n.t("project-benchmark.section-benchmark")} (${displayName})`,
            value: bmVal,
            color: benchmarkColor,
            lineDashStyle: [8, 4],
            barIndices: indices,
            shortLabel: i18n.t("project-benchmark.section-benchmark"),
            typeLabel: displayName,
            unit: unitLabel,
          });
        }

        // Match target value by modelTypeEntityId FK
        const tgt = targetValues.find((t) => t.modelTypeEntityId === modelTypeId);
        const tgtVal = toValue(tgt?.valueKgCo2ePerM2);
        if (tgtVal != null) {
          result.push({
            label: `${i18n.t("project-benchmark.section-targets")} (${displayName})`,
            value: tgtVal,
            color: targetColor,
            lineDashStyle: [4, 4],
            barIndices: indices,
            shortLabel: i18n.t("project-benchmark.section-targets"),
            typeLabel: displayName,
            unit: unitLabel,
          });
        }
      }
    } else {
      // By Scenario: WholeBuilding overlays
      const wbBm = benchmarkValues.find((v) => v.scope === "WholeBuilding");
      const bmVal = toValue(wbBm?.valueKgCo2ePerM2);
      if (bmVal != null) {
        result.push({
          label: i18n.t("project-benchmark.section-benchmark"),
          value: bmVal,
          color: benchmarkColor,
          lineDashStyle: [8, 4],
          shortLabel: i18n.t("project-benchmark.section-benchmark"),
          unit: unitLabel,
        });
      }

      const wbTgt = targetValues.find((t) => t.scope === "WholeBuilding");
      const tgtVal = toValue(wbTgt?.valueKgCo2ePerM2);
      if (tgtVal != null) {
        result.push({
          label: i18n.t("project-benchmark.section-targets"),
          value: tgtVal,
          color: targetColor,
          lineDashStyle: [4, 4],
          shortLabel: i18n.t("project-benchmark.section-targets"),
          unit: unitLabel,
        });
      }
    }

    return result;
  }, [
    benchmarkData,
    projectData,
    projectAnalysis.selectedUnitType,
    projectAnalysis.isGroupTypeModel,
    hasAdditionalMeta,
    analysisType,
    benchmarkColor,
    targetColor,
  ]);
}
